/**
 * A minimal modal — the 2.5 stand-in for `bimgo-ai-web`'s shadcn `Dialog`, which
 * we do not have. A React overlay (not a native `alert`/`confirm`, which would
 * block the renderer), closable by the backdrop, the ✕, or Escape.
 */
import { useEffect } from "react";

export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  description,
  icon,
  wide = false,
  size,
  actions,
  titleAdornment,
  closable = true,
  showClose = true,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * When false, the dialog cannot be dismissed — the ✕ is disabled and the backdrop and Escape
   * do nothing. For a modal running an operation the user must not interrupt or lose track of.
   */
  closable?: boolean;
  /** When false, the ✕ is not rendered — for a dialog whose only way out is its footer button. */
  showClose?: boolean;
  /** Sub-line under the title — the web client's `DialogDescription`. */
  description?: string | undefined;
  /** A small element rendered inline right after the title text — e.g. an info icon. */
  titleAdornment?: React.ReactNode;
  /** Optional glyph left of the heading — the delete dialog's red warning disc. */
  icon?: React.ReactNode;
  /** `max-w-lg` rather than the default — the permissions view needs the room. */
  wide?: boolean;
  /**
   * The old web's two large fixed-height dialogs: `4xl` is `JsonViewerDialog`
   * (`max-w-4xl h-[70vh]`), `5xl` the data-tables dialog (`max-w-5xl h-[80vh]`).
   * The body becomes a flex column that the content scrolls inside of.
   */
  size?: "4xl" | "5xl";
  /** Controls in the header, right of the title — the old ToolEditor's Save Skill. */
  actions?: React.ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && closable) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, closable]);

  if (!open) return null;

  return (
    <div className="modal-overlay" onClick={closable ? onClose : undefined}>
      <div
        className={`modal-card${wide ? " is-wide" : ""}${size ? ` is-${size} is-fixed` : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          {icon ? <div className="modal-icon">{icon}</div> : null}
          <div className="modal-heading">
            <h3>
              {title}
              {titleAdornment ? <span className="modal-title-adornment">{titleAdornment}</span> : null}
            </h3>
            {description ? <p className="modal-desc">{description}</p> : null}
          </div>
          {actions ? <div className="modal-actions">{actions}</div> : null}
          {showClose ? (
            <button
              type="button"
              className="modal-x"
              onClick={onClose}
              disabled={!closable}
              aria-label="Close"
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="modal-body">{children}</div>
        {footer ? <div className="modal-foot">{footer}</div> : null}
      </div>
    </div>
  );
}
